// THE http client. Every request in the app goes through it, so a new auth
// scheme, base URL or request log is a one-file change. FRONTEND_RULE_BOOK.md §3.
//
// Auth is inverted on purpose. The bearer token lives in the Stytch SDK
// (`stytch.session.getTokens()`), never in localStorage — but `shared/` may not
// import a feature, so this file cannot reach for it. Instead `features/auth`
// pushes two callbacks in at provider mount:
//
//   setTokenSource(getJwt)        → the request interceptor's bearer token
//   setSessionRecovery(recover)   → one re-mint + retry on a 401
//
// Unregistered (tests, Storybook, any app without auth mounted) both default to
// "no token, no recovery" and requests go out bare.
import axios, { type AxiosRequestConfig } from "axios";

import { normalizeError } from "./errors";

export const client = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15_000,
});

/** Every feature uses the configured shared API origin. */
export function baseUrlFor(_path: string): string {
  return client.defaults.baseURL || "";
}

let getToken: () => string | null = () => null;
let recoverSession: (() => Promise<boolean>) | null = null;

/** Register the bearer-token getter. Called by `features/auth`'s AuthProvider. */
export function setTokenSource(source: () => string | null): void {
  getToken = source;
}

/**
 * Register the 401 recovery step: re-mint the session and resolve `true` to have
 * the failed request retried once. Resolving `false` means the session is gone —
 * the recovery callback owns revoking it and sending the user to `/login`.
 */
export function setSessionRecovery(recover: () => Promise<boolean>): void {
  recoverSession = recover;
}

/** The bearer token itself, for the socket (`socket.ts`), which carries it as a
    subprotocol because a browser WebSocket cannot set a header. */
export function currentToken(): string | null {
  return getToken();
}

/** The bearer header, for the one request axios cannot make: a streamed
    response body (`stream.ts`). Everything else goes through `client`. */
export function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

client.interceptors.request.use((config) => {
  Object.assign(config.headers, authHeaders());
  config.baseURL = baseUrlFor(config.url ?? "");
  return config;
});

// Retried requests are tagged so a 401 on the retry falls straight through to
// the caller instead of looping.
type Retryable = AxiosRequestConfig & { _retried?: boolean };

client.interceptors.response.use(
  (res) => res,
  async (err) => {
    const config = err?.config as Retryable | undefined;
    if (err?.response?.status === 401 && recoverSession && config && !config._retried) {
      config._retried = true;
      if (await recoverSession()) return client(config);
    }
    return Promise.reject(normalizeError(err));
  },
);
